import { useState, useEffect, useCallback } from 'react';
import { planService, updatePlanFeatures } from '../services/planService';

/**
 * Hook personnalisé pour gérer l'état du plan utilisateur
 * @returns {object} État et méthodes pour gérer le plan
 */
const usePlan = () => {
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // État pour éviter la réinitialisation du plan lors des navigations
  const [lastKnownPlan, setLastKnownPlan] = useState(() => {
    // Essayer de récupérer le dernier plan connu depuis sessionStorage
    try {
      return sessionStorage.getItem('lastKnownPlan') || 'decouverte';
    } catch {
      return 'decouverte';
    }
  });

  /**
   * Charger les données du plan et des plans disponibles
   */
  const loadPlanData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Charger la configuration des plans depuis l'API
      await updatePlanFeatures();
      
      // Charger les données utilisateur
      const userData = await planService.loadUserSettings();
      console.log('userData', userData);
      setPlanData(userData);
      
      // Sauvegarder le plan actuel pour éviter la réinitialisation
      if (userData?.billing_plan) {
        setLastKnownPlan(userData.billing_plan);
        try {
          sessionStorage.setItem('lastKnownPlan', userData.billing_plan);
        } catch (e) {
          console.warn('Impossible de sauvegarder le plan dans sessionStorage:', e);
        }
      }
      
    } catch (err) {
      console.error('Erreur lors du chargement des données de plan:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Vérifier les limites de messages
   */
  const checkMessageLimit = useCallback(async () => {
    return await planService.checkMessageLimit();
  }, []);

  /**
   * Vérifier si une fonctionnalité est disponible
   */
  const hasFeature = useCallback(async (feature) => {
    return await planService.hasFeature(feature);
  }, []);

  /**
   * Vérifier si l'utilisateur peut créer un élément
   */
  const canCreate = useCallback(async (type, currentCount) => {
    return await planService.canCreate(type, currentCount);
  }, []);

  /**
   * Vérifier les limites génériques
   */
  const checkLimit = useCallback(async (limitType, currentCount) => {
    return await planService.checkLimit(limitType, currentCount);
  }, []);

  /**
   * Recharger les données du plan
   */
  const refreshPlan = useCallback(async () => {
    setLoading(true);
    try {
      const userData = await planService.refresh();
      setPlanData(userData);
      
      // Sauvegarder le plan mis à jour
      if (userData?.billing_plan) {
        setLastKnownPlan(userData.billing_plan);
        try {
          sessionStorage.setItem('lastKnownPlan', userData.billing_plan);
        } catch (e) {
          console.warn('Impossible de sauvegarder le plan dans sessionStorage:', e);
        }
      }
      
      // Mettre à jour aussi PLAN_FEATURES
      await updatePlanFeatures();
    } catch (err) {
      console.error('Erreur lors du rafraîchissement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les données au montage du composant
  useEffect(() => {
    loadPlanData();
  }, [loadPlanData]);

  console.log('planData', planData);
  console.log('lastKnownPlan', lastKnownPlan);
  
  // Computed values - utiliser le dernier plan connu si planData n'est pas encore chargé
  const currentPlan = planData?.billing_plan || lastKnownPlan;
  const planName = planData ? (() => {
    // Utiliser une fonction async dans un useEffect pour obtenir le nom
    planService.getPlanName(currentPlan).then(name => {
      // On ne peut pas mettre à jour le state ici car c'est dans un computed
      // Donc on retourne juste le plan en cours
    });
    return currentPlan; // fallback
  })() : 'Découverte';
  
  const isTrialPlan = planData?.is_trial || false;
  const trialMessagesUsed = planData?.trial_messages_used || 0;
  const trialMaxMessages = planData?.trial_max_messages || 15;

  return {
    // État
    planData,
    loading,
    error,
    
    // Données calculées
    currentPlan,
    planName,
    isTrialPlan,
    trialMessagesUsed,
    trialMaxMessages,
    planFeatures: planData?.plan_features,
    
    // Méthodes
    refreshPlan,
    checkMessageLimit,
    hasFeature,
    canCreate,
    checkLimit,
    
    // Utilitaires
    reload: loadPlanData
  };
};

export default usePlan; 